/// <amd-module name="VSS/Features/Charts/Table" />
import * as Highcharts from "highcharts";
import React = require("react");
import { format } from "VSS/Core/Util/String";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import * as Internal_Contracts from "VSS/Features/Charts/Internal/InternalContracts";

import { TabularLabelFormatter } from "VSS/Features/Charts/Internal/AxisLabelFormatter";
import { TooltipFormatterPoint } from "VSS/Features/Charts/Internal/TooltipFormatter";

import { ChartTypesRegistry } from "VSS/Features/Charts/Internal/ChartTypesRegistry";
import { CommonChartImplementation } from "VSS/Features/Charts/Internal/CommonChartImplementation";
import * as Charts_Resources from "VSS/Features/Charts/Resources";
import { AddressedTabularComponent } from "./AddressedTabularComponent";

/** A point object with additional fields specific to Heatmap */
interface HeatmapExtendedPointObject extends Internal_Contracts.HighchartsExtendedPointObject {
    /** The value of the data point. */
    value: number;
}

class TableImplementation extends CommonChartImplementation {
    public constructor() {
        super();

        // workaround for issue : https://github.com/highcharts/highcharts/issues/13049 as suggested in the issue.
        // to fix an accessibility bug #1667446
        (Highcharts as any).seriesTypes.heatmap.prototype.pointClass.prototype.setState = function(state?: any) {
            Highcharts.Point.prototype.setState.call(this, state);
        };
    }

    /** Default Implementation for rendering chart options in a textual-tabular representation for screen readers.*/
    public getTabularComponent(chartOptions: Charts_Contracts.ChartProps): JSX.Element {
        return React.createElement(AddressedTabularComponent, chartOptions);
    }

    public mapSeriesType(chartType: string, index?: number): string {
        return "heatmap"; // We're using a different name, because this chart is not for use as a traditional heat map.
    }

    public translatePlotOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.PlotOptions {
        const plotOptions: Highcharts.PlotOptions = super.translatePlotOptions(chartOptions);

        plotOptions.heatmap = {
            dataLabels: {
                enabled: true,
                style: {
                    textOutline: "false",
                    fontWeight: "normal"
                },
                formatter: TabularLabelFormatter.getFormatter()
            }
        };
        return plotOptions;
    }

    public translateOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.Options {
        const highChartOptions = super.translateOptions(chartOptions);
        highChartOptions.accessibility = _translateAccessibilityOptions(chartOptions, highChartOptions.accessibility);
        return highChartOptions;
    }

    public mapDataPointsToTooltipLineItems(points: Charts_Contracts.DataPoint[]): Charts_Contracts.TooltipLineItem[] {
        // We want to use the default mapping function but need to change the series name
        const modifiedPoints = points.map<Charts_Contracts.DataPoint>(point => {
            const value = point.values[0];
            const xAxisCategory = point.values[1];

            return {
                color: point.color,
                seriesName: xAxisCategory,
                seriesCustomData: point.seriesCustomData,
                seriesDataIndex: point.seriesDataIndex,
                values: [value]
            } as Charts_Contracts.DataPoint;
        });

        return super.mapDataPointsToTooltipLineItems(modifiedPoints);
    }

    public getTooltipHeaderValue(formatterPoint: TooltipFormatterPoint): string | number {
        // The header should be the y-axis category
        return formatterPoint.series.yAxis.categories[formatterPoint.point.y];
    }

    public interpretPointAsValuesForTooltip(point: HeatmapExtendedPointObject): (string | number)[] {
        // [value, x-axis category]
        return [point.value, point.series.xAxis.categories[point.x]];
    }
}

// export for UTs
export function _translateAccessibilityOptions(
    chartOptions: Charts_Contracts.ChartProps,
    accessibility: Highcharts.AccessibilityOptions
): Highcharts.AccessibilityOptions {
    if (!accessibility) {
        accessibility = {};
    }

    // we're using heatmap behind the scenes to render a table and the default aria-label for heatmap doesn't work well with pivot table.
    // so, adding a custom aria label of type "point-index. xLabel yLabel, value"
    if(!accessibility.point) {
        accessibility.point = {};
    }
    accessibility.point.descriptionFormatter = (point: HeatmapExtendedPointObject): string => {
        const xValue = chartOptions.xAxis.labelValues[point.x];
        const formattedXValue = xValue instanceof Date ? xValue.toDateString() : xValue;
        const yValue = chartOptions.yAxis.labelValues[point.y];
        const formattedYValue = yValue instanceof Date ? yValue.toDateString() : yValue;

        let positionLabel = formattedXValue + " " + formattedYValue;
        positionLabel = point.index != null ? format(Charts_Resources.A11yLabelPrefixFormat, point.index + 1, positionLabel) : positionLabel;

        const roundingAmount = Math.pow(10, 2); // Two significant digits.
        const value = Math.round(point.value * roundingAmount) / roundingAmount;

        return format(Charts_Resources.A11yLabelElementSeparatorFormat, positionLabel, value);
    };

    return accessibility;
}

class PivotTableImplementation extends TableImplementation {
    public translateLegendOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.LegendOptions {
        return super.translateLegendOptions(chartOptions, { enabled: false });
    }

    public translateAxisArrayOptions(options: Charts_Contracts.AxisOptions[]): Highcharts.AxisOptions[] {
        const highchartsAxisArrayOptions = super.translateAxisArrayOptions(options);

        // Move the X-Axis to be labeled from the top
        if (options.length == 1) {
            highchartsAxisArrayOptions[0].opposite = true;
        } else {
            highchartsAxisArrayOptions[0].reversed = true;
        }

        return highchartsAxisArrayOptions;
    }

    public getColoringNames(chartOptions: Charts_Contracts.ChartProps): string[] {
        return super.getColoringNames(chartOptions).slice(0, -1);
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Table,
    constructor: PivotTableImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Table
});
